import React, { useMemo, useState } from "react";
import "./Helpsupport.css";

export default function HelpSupport() {
  // ----- FAQ data (can be swapped with API data later) -----
  const allFaqs = useMemo(
    () => [
      {
        id: "f1",
        q: "How do I create and assign a delivery job?",
        a: "From the dashboard, open Jobs and click 'Create Job'. Enter the pickup and drop-off details, then assign an available driver — or let the system suggest the best driver for the route. You can reassign a job any time before pickup.",
      },
      {
        id: "f2",
        q: "How can I track my deliveries in real time?",
        a: "Every active job appears on the live map. You can follow each driver's location, see estimated arrival times, and watch stops update as they are completed. Completed routes can also be replayed later.",
      },
      {
        id: "f3",
        q: "What is GPS-stamped proof of delivery?",
        a: "When a driver completes a delivery, the app captures a photo tagged with the exact GPS location and timestamp — no signature required. This creates tamper-evident proof you can share or download at any time.",
      },
      {
        id: "f4",
        q: "Can my customers track their own deliveries?",
        a: "Yes. Each delivery generates a branded tracking link you can share with your customer. It shows live status, estimated arrival, and photo proof of delivery once completed.",
      },
      {
        id: "f5",
        q: "How do I download proof-of-delivery reports?",
        a: "Open any completed job and select 'Download Proof' to export a PDF with the delivery photo, GPS location, and timestamp. You can also export job data to CSV or Excel from the Jobs page.",
      },
      {
        id: "f6",
        q: "How do I add or manage drivers?",
        a: "Go to the Drivers section to invite new drivers, view their status, and monitor performance. Drivers sign in through the Marquee Couriers driver app to receive and complete their assigned routes.",
      },
    ],
    []
  );

  // ----- Search + accordion state -----
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allFaqs;
    return allFaqs.filter(
      (f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)
    );
  }, [allFaqs, query]);

  const toggle = (id) => setOpenId((prev) => (prev === id ? null : id));

  return (
    <div className="help-container">
      <div className="mq-welcome-block">
        <div className="mq-welcome-label">← Back Home</div>
        <h1 className="mq-welcome-name">Help & Support</h1>
      </div>
      {/* Hero / search card */}
      <div className="help-hero card">
        <div className="help-hero-inner">
          <h2 className="hero-title">Need Assistance?</h2>
          <p className="hero-subtitle">
            Search our FAQs or reach out to our team — we're here to help you get
            the most out of Marquee Couriers.
          </p>

          <div className="hero-search">
            <div className="search-input-wrap">
              <svg
                className="search-icon"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z"
                  stroke="#9CA3AF"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14 14L11.1 11.1"
                  stroke="#9CA3AF"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search"
                aria-label="Search help articles and FAQs"
              />
            </div>

            <button
              className="submit-btn search-btn"
              onClick={() => {
                // optional: hook this up to a backend search later
              }}
            >
              Search Now
              <svg
                className="arrow-icon"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.33337 8H12.6667"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 3.33334L12.6667 8.00001L8 12.6667"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* FAQ list */}
      <div className="card">
        <div className="card-head">Frequently Asked Questions</div>

        <ul className="faq-list">
          {filtered.map((item) => {
            const open = openId === item.id;
            return (
              <li key={item.id} className={`faq-item ${open ? "open" : ""}`}>
                <button
                  className="faq-toggle"
                  aria-expanded={open}
                  aria-controls={`faq-panel-${item.id}`}
                  onClick={() => toggle(item.id)}
                >
                  <span className="faq-q">{item.q}</span>
                  <span className="faq-plus" aria-hidden>
                    {open ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M19.002 12.002H5" stroke="#141B34" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5V19.002" stroke="#141B34" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M19.002 12.002H5" stroke="#141B34" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </span>
                </button>

                <div
                  id={`faq-panel-${item.id}`}
                  role="region"
                  aria-labelledby={`faq-btn-${item.id}`}
                  className="faq-panel"
                >
                  <p className="faq-a">{item.a}</p>
                </div>
              </li>
            );
          })}

          {filtered.length === 0 && (
            <li className="faq-empty">
              No results for "{query}". Try a different keyword.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}
